import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useFilters } from "@/context/FiltersContext";
import { useAuth } from "@/context/AuthContext";
import { ASSOCIATIONS, EVENT_CATEGORIES } from "@/lib/ui";
import useRegions from "@/lib/useRegions";
import { Check, Clock } from "lucide-react";

export default function MobileFilters({ open, onClose }) {
  const { assoc, toggleAssoc, events, toggleEvent, minMoney, setMinMoney, status, setStatus, country, setCountry, province, setProvince, circuits, toggleCircuit, clearCircuits, rodeos, closingSoon, toggleClosingSoon, sortBy, setSortBy } = useFilters();
  const { user } = useAuth();
  const eventsLabel = user?.account_type === "fan" ? "Events I Watch" : "Events I Enter";
  const regions = useRegions();
  const provincesForCountry = (regions.find((r) => r.country === country)?.provinces) || [];
  const allCircuits = [...new Set((rodeos || []).map((r) => r.circuit_name).filter(Boolean))].sort();

  const Chk = ({ label, on, onClick, testid }) => (
    <button onClick={onClick} data-testid={testid}
      className="w-full flex items-center gap-2.5 px-1 py-2 text-left">
      <span className={`w-5 h-5 rounded flex items-center justify-center border ${on ? "bg-[#C8000F] border-[#C8000F]" : "border-slate-600"}`}>
        {on && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
      </span>
      <span className="text-sm text-slate-200">{label}</span>
    </button>
  );

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="bottom" className="bg-[#0C1017] text-slate-100 border-t border-white/10 rounded-t-2xl max-h-[85vh] overflow-y-auto" data-testid="mobile-filters-sheet">
        <SheetHeader>
          <SheetTitle className="text-white font-display text-lg">Filters</SheetTitle>
        </SheetHeader>

        <button onClick={toggleClosingSoon} data-testid="mf-closing-soon"
          className={`mt-3 w-full flex items-center justify-between gap-2 px-3.5 py-3 rounded-xl border transition-colors ${closingSoon ? "bg-[#C8000F] border-[#C8000F] text-white" : "bg-white/5 border-white/10 text-slate-200"}`}>
          <span className="flex items-center gap-2 text-sm font-bold"><Clock className="w-4 h-4" /> Closes within 7 days</span>
          <span className={`w-5 h-5 rounded flex items-center justify-center border ${closingSoon ? "bg-white border-white" : "border-slate-500"}`}>
            {closingSoon && <Check className="w-3.5 h-3.5 text-[#C8000F]" strokeWidth={3} />}
          </span>
        </button>

        <div className="mt-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Country</div>
          <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="mf-country"
            className="w-full bg-white/5 text-slate-200 text-sm rounded-lg px-3 py-2.5 border border-white/10 focus:outline-none">
            <option value="" className="bg-[#0C1017]">All countries</option>
            {regions.map((r) => <option key={r.country} value={r.country} className="bg-[#0C1017]">{r.country} ({r.count})</option>)}
          </select>
          {country && (
            <select value={province} onChange={(e) => setProvince(e.target.value)} data-testid="mf-province"
              className="w-full mt-2 bg-white/5 text-slate-200 text-sm rounded-lg px-3 py-2.5 border border-white/10 focus:outline-none">
              <option value="" className="bg-[#0C1017]">All provinces</option>
              {provincesForCountry.map((p) => <option key={p.province} value={p.province} className="bg-[#0C1017]">{p.province} ({p.count})</option>)}
            </select>
          )}
        </div>

        <div className="mt-5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Entries</div>
          <div className="flex flex-wrap gap-2">
            {[{ v: "", l: "All" }, { v: "upcoming", l: "Entries Open" }, { v: "closed", l: "Entries Closed" }].map((s) => (
              <button key={s.v} onClick={() => setStatus(s.v)} data-testid={`mf-status-${s.v || "all"}`}
                className={`text-sm px-3.5 py-1.5 rounded-full ${status === s.v ? "bg-[#C8000F] text-white" : "bg-white/5 text-slate-300"}`}>{s.l}</button>
            ))}
          </div>
        </div>

        <div className="mt-5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Added Money</div>
          <div className="flex flex-wrap gap-2">
            {[0, 7500, 15000, 30000].map((m) => (
              <button key={m} onClick={() => setMinMoney(m)} data-testid={`mf-money-${m}`}
                className={`text-sm px-3.5 py-1.5 rounded-full ${minMoney === m ? "bg-[#C8000F] text-white" : "bg-white/5 text-slate-300"}`}>{m === 0 ? "Any" : `$${m / 1000}k+`}</button>
            ))}
          </div>
        </div>

        <div className="mt-5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">My Associations</div>
          {ASSOCIATIONS.map((a) => <Chk key={a} label={a} on={assoc.has(a)} onClick={() => toggleAssoc(a)} testid={`mf-assoc-${a}`} />)}
        </div>

        {allCircuits.length > 0 && (
          <div className="mt-5">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">PRCA Circuit</div>
              <div className="flex items-center gap-3">
                <button onClick={() => (circuits.size === allCircuits.length ? clearCircuits() : allCircuits.forEach((c) => { if (!circuits.has(c)) toggleCircuit(c); }))} data-testid="mf-circuit-select-all" className="text-[11px] font-bold uppercase tracking-wide text-[#3B82F6]">
                  {circuits.size === allCircuits.length ? "Deselect all" : "Select all"}
                </button>
                {circuits.size > 0 && <button onClick={clearCircuits} data-testid="mf-circuit-clear" className="text-[11px] font-semibold text-[#F26D77]">Clear</button>}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {allCircuits.map((c) => {
                const on = circuits.has(c);
                return (
                  <button key={c} onClick={() => toggleCircuit(c)} data-testid={`mf-circuit-${c.replace(/[\s()]/g, "-")}`}
                    className={`text-[12px] font-semibold px-3 py-1.5 rounded-full border ${on ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "bg-white/5 text-slate-300 border-white/10"}`}>{c}</button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-4 mb-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{eventsLabel}</div>
          {EVENT_CATEGORIES.map((e) => <Chk key={e} label={e} on={events.has(e)} onClick={() => toggleEvent(e)} testid={`mf-event-${e.replace(/[\s()]/g, "-")}`} />)}
        </div>

        <div className="mt-5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Sort by</div>
          <div className="flex flex-wrap gap-2">
            {[{ v: "date", l: "By date" }, { v: "near", l: "Nearest" }, { v: "money", l: "Biggest purse" }, { v: "value", l: "Best $/mi" }].map((o) => {
              const on = (sortBy === o.v) || (o.v === "date" && (sortBy === "default" || !sortBy));
              return (
                <button key={o.v} onClick={() => setSortBy(o.v)} data-testid={`mf-sort-${o.v}`}
                  className={`text-sm px-3.5 py-1.5 rounded-full ${on ? "bg-[#C8000F] text-white" : "bg-white/5 text-slate-300"}`}>{o.l}</button>
              );
            })}
          </div>
        </div>

        <button onClick={onClose} data-testid="mf-apply" className="w-full mt-3 bg-[#C8000F] hover:bg-[#9E0008] text-white font-semibold py-3 rounded-xl">Show Rodeos</button>
      </SheetContent>
    </Sheet>
  );
}
